import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

const REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref');
const REACT_MEMO_TYPE = Symbol.for('react.memo');

const canReceiveDomRef = type =>
  typeof type === 'string' ||
  type?.$$typeof === REACT_FORWARD_REF_TYPE ||
  (type?.$$typeof === REACT_MEMO_TYPE && type.type?.$$typeof === REACT_FORWARD_REF_TYPE);

const assignRef = (ref, value) => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    const refObject = ref;
    refObject.current = value;
  }
};

export function CSSTransitionWithRef({ children, nodeRef = null, ...restProps }) {
  const ref = useRef(null);
  const TransitionChild = children.type;
  const useWrapper = !nodeRef && !canReceiveDomRef(TransitionChild);
  // React 19 exposes a child's ref through props, so compose it instead of overwriting it.
  const childRef = children.props.ref;
  const mergedRef = useCallback(
    value => {
      ref.current = value;
      assignRef(childRef, value);
    },
    [childRef]
  );
  let transitionChild;

  if (useWrapper) {
    transitionChild = <div ref={ref}>{children}</div>;
  } else if (nodeRef) {
    transitionChild = children;
  } else {
    transitionChild = <TransitionChild {...children.props} ref={mergedRef} />;
  }

  return (
    <CSSTransition nodeRef={nodeRef ?? ref} {...restProps}>
      {transitionChild}
    </CSSTransition>
  );
}

CSSTransitionWithRef.propTypes = {
  children: PropTypes.element.isRequired,
  nodeRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
};
